<template>
  <v-container fluid>
    <!-- Global loading overlay while dictionary entries are being fetched -->
    <loading-overlay :visible="loading" :label="$t('states.loading') + '\u2026'" />
    <div class="dictionary">
      <div class="dictionary-title-row">
        <h2>{{ $t("app.dictionary.title") }}</h2>
        <div v-if="isTranslated" class="language-select-wrap">
          <v-select
            v-model="languageView"
            :items="languageOptions"
            item-text="label"
            item-value="value"
            outlined
            dense
            hide-details
          />
        </div>
      </div>

      <p v-if="isTranslated && languageView === 'translated'" class="translated-notice">
        {{ $t("app.dictionary.translated-notice") }}
      </p>

      <InputField
        v-model="query"
        @input.native="filterEntries(query)"
        unit-icon="search-pink"
        unit-first
        type="text"
        :placeholder="$t('app.dictionary.search')"
        width="100%"
        margin="24px auto 16px auto"
      ></InputField>

      <div v-if="isTranslated" class="search-scope-row">
        <span class="dictionary-option-label">
          {{ $t("app.dictionary.search-options") }}
        </span>
        <v-chip
          v-for="opt in searchScopeOptions"
          :key="opt.value"
          x-small
          :color="searchScope === opt.value ? '#00A9B3' : ''"
          :outlined="searchScope !== opt.value"
          :dark="searchScope === opt.value"
          class="scope-chip"
          @click="onSearchScopeChange(opt.value)"
        >
          {{ opt.label }}
        </v-chip>
      </div>
    </div>
    <div class="dictionary-entries top">
      <div class="center">
        <h2>
          {{
            `${$t("app.dictionary.personal-info")} ${
              this.$store.getters.user.patient.firstName
            }`
          }}
        </h2>
      </div>
      <div v-if="filteredPersonalDictionary.length > 0">
        <div v-for="entry in filteredPersonalDictionary" :key="entry.id">
          <DetailListItem
            :id="`dictionary-entry${entry.id}`"
            :class="{ 'active-entry': isContentOpen(`entrypersonal${entry.id}`) }"
            @click.native="collapseContent(`entrypersonal${entry.id}`, entry.id)"
            height="auto"
            icon-right
            icon="arrow-down-pink.svg"
            :icon-class="getEntryIconClass(`entrypersonal${entry.id}`)"
            >{{ entry.title }}</DetailListItem
          >

          <div :id="`entrypersonal${entry.id}-content`" class="content-closed">
            <div class="entry-content">
              <div>
                <p class="description" v-html="linkify(entry.description)" />
                <div class="uploads">
                  <div
                    v-for="upload in entry.uploads"
                    :key="upload.id"
                    class="upload-container"
                  >
                    <video
                      controls
                      v-if="upload.mimeType.includes('video/')"
                      :src="upload.blobUri"
                      :alt="upload.originalname"
                    />
                    <auto-rotate v-else>
                      <img
                        :src="upload.blobUri"
                        :alt="upload.originalname"
                      />
                    </auto-rotate>
                  </div>
                </div>
                <div v-if="entry.tags.length > 0" class="tags">
                  <span
                    v-ripple="'rgb(229, 115, 120, 0.05)'"
                    @click="
                      searchScope = isTranslated ? 'both' : 'default';
                      query = tag.name;
                      filterEntries(tag.name);
                    "
                    v-for="tag in entry.tags"
                    :key="tag.id"
                    class="tag"
                    >#{{ tag.name }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="center">
        <p v-html="$t('app.dictionary.not-found')"></p>
      </div>
    </div>

    <div class="dictionary-entries bottom">
      <div class="center">
        <h2>{{ $t("app.dictionary.generic-info") }}</h2>
      </div>
      <div v-if="filteredDictionary.length > 0">
        <div v-for="entry in filteredDictionary" :key="entry.id">
          <DetailListItem
            :id="`dictionary-entry${entry.id}`"
            :class="{ 'active-entry': isContentOpen(`entry${entry.id}`) }"
            @click.native="collapseContent(`entry${entry.id}`, entry.id)"
            height="auto"
            icon-right
            icon="arrow-down-pink.svg"
            :icon-class="getEntryIconClass(`entry${entry.id}`)"
            >{{ entry.title }}</DetailListItem
          >

          <div :id="`entry${entry.id}-content`" class="content-closed">
            <div class="entry-content">
              <div>
                <p class="description" v-html="linkify(entry.description)" />
                <div class="uploads">
                  <div
                    v-for="upload in entry.uploads"
                    :key="upload.id"
                    class="upload-container"
                  >
                    <video
                      controls
                      v-if="upload.mimeType.includes('video/')"
                      :src="upload.blobUri"
                      :alt="upload.originalname"
                    />
                    <auto-rotate v-else>
                      <img
                        :src="upload.blobUri"
                        :alt="upload.originalname"
                      />
                    </auto-rotate>
                  </div>
                </div>
                <div v-if="entry.tags.length > 0" class="tags">
                  <span
                    v-ripple="'rgb(229, 115, 120, 0.05)'"
                    @click="
                      searchScope = isTranslated ? 'both' : 'default';
                      query = tag.name;
                      filterEntries(tag.name);
                    "
                    v-for="tag in entry.tags"
                    :key="tag.id"
                    class="tag"
                    >#{{ tag.name }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="center">
        <p v-html="$t('app.dictionary.not-found')"></p>
      </div></div
  ></v-container>
</template>

<script>
import { neoparentDictionaryEntries } from "@/api";
import linkifyStr from "linkifyjs/string";

export default {
  name: "neoparent-dictionary",
  data() {
    return {
      loading: true,
      personalDictionary: [],
      filteredPersonalDictionary: [],
      dictionary: [],
      filteredDictionary: [],
      query: "",
      languageView: "default",
      searchScope: "default",
      personalDictionaryDefault: [],
      personalDictionaryTranslated: [],
      dictionaryDefault: [],
      dictionaryTranslated: [],
      // Tracks open state per collapsible content key (e.g. "entry<id>")
      openEntryStates: {}
    };
  },
  watch: {
    languageView(val, oldVal) {
      if (val !== oldVal) this.applyCurrentViewAndFilter();
    }
  },

  computed: {
    // True when the user has a non-Dutch translation language set
    isTranslated() {
      const lang = this.$store.getters.user && this.$store.getters.user.translationLanguage;
      return !!(lang && lang !== "nl");
    },
    translatedLanguageCode() {
      const lang = this.$store.getters.user && this.$store.getters.user.translationLanguage;
      if (!lang) {
        return "TR";
      }
      return String(lang).toUpperCase();
    },
    languageOptions() {
      const nlLabel = this.$te("general.languages.nl")
        ? this.$t("general.languages.nl")
        : "NL";
      const opts = [{ value: "default", label: nlLabel }];
      if (this.isTranslated) {
        const code = this.$store.getters.user.translationLanguage || "";
        const label = code && this.$te("general.languages." + code)
          ? this.$t("general.languages." + code)
          : this.translatedLanguageCode;
        opts.push({ value: "translated", label });
      }
      return opts;
    },
    searchScopeOptions() {
      return [
        { value: "default", label: this.$t("app.dictionary.search-scope-default") },
        { value: "translated", label: this.$t("app.dictionary.search-scope-translated") },
        { value: "both", label: this.$t("app.dictionary.search-scope-both") }
      ];
    }
  },
  async mounted() {
    this.languageView = this.isTranslated ? "translated" : "default";
    this.searchScope = this.isTranslated ? "both" : "default";

    try {
      await this.loadDictionaryData();
    } finally {
      this.loading = false;
    }

    // Check query for filtering
    const url = new URL(window.location);
    const entryId = url.searchParams.get("entryId");
    if (entryId) {
      const inPersonal = this.personalDictionary.some(e => e.id == entryId);
      const inGeneric  = this.dictionary.some(e => e.id == entryId);
      if (inPersonal || inGeneric) {
        // Use the correct content-div prefix depending on which section owns the entry
        const prefix = inPersonal ? `entrypersonal${entryId}` : `entry${entryId}`;
        this.collapseContent(prefix, entryId);
        this.$nextTick(() => {
          const el = document.getElementById(`dictionary-entry${entryId}`);
          if (el) window.scroll(0, el.offsetTop - 70);
        });
      }
    }
    const searchParam = url.searchParams.get("search");
    const tagIdParam = url.searchParams.get("tagId");
    if (searchParam || tagIdParam) {
      this.query = this.resolveSearchByTagId(tagIdParam, searchParam);
      await this.filterEntries(this.query);
      const all = document.querySelectorAll(".content-closed");
      for (let i = 0; i < all.length; i++) {
        all[i].classList.remove("content-closed");
      }

      this.filteredPersonalDictionary.forEach(entry => {
        this.$set(this.openEntryStates, `entrypersonal${entry.id}`, true);
      });
      this.filteredDictionary.forEach(entry => {
        this.$set(this.openEntryStates, `entry${entry.id}`, true);
      });
    }
  },
  methods: {
    async loadDictionaryData() {
      if (this.isTranslated) {
        const [genericTranslated, personalTranslated, genericDefault, personalDefault] = await Promise.all([
          this.fetchGenericEntries(true),
          this.fetchPersonalEntries(true),
          this.fetchGenericEntries(false),
          this.fetchPersonalEntries(false)
        ]);

        this.personalDictionaryTranslated = personalTranslated;
        this.dictionaryTranslated = this.removePersonalEntriesFromGeneric(personalTranslated, genericTranslated);
        this.personalDictionaryDefault = personalDefault;
        this.dictionaryDefault = this.removePersonalEntriesFromGeneric(personalDefault, genericDefault);
      } else {
        const [genericDefault, personalDefault] = await Promise.all([
          this.fetchGenericEntries(false),
          this.fetchPersonalEntries(false)
        ]);

        this.personalDictionaryDefault = personalDefault;
        this.dictionaryDefault = this.removePersonalEntriesFromGeneric(personalDefault, genericDefault);
        this.personalDictionaryTranslated = [...this.personalDictionaryDefault];
        this.dictionaryTranslated = [...this.dictionaryDefault];
      }

      this.applyCurrentViewAndFilter();
    },

    async fetchGenericEntries(useUserTranslation) {
      try {
        const result = await neoparentDictionaryEntries.getAll("", false, useUserTranslation);
        return result?.data?.entries || [];
      } catch (err) {
        this.consoleLog(err);
        return [];
      }
    },

    async fetchPersonalEntries(useUserTranslation) {
      try {
        const result = await neoparentDictionaryEntries.getAllForPatient(
          this.$store.getters.user.patient.id,
          useUserTranslation
        );
        return result?.data || [];
      } catch (err) {
        this.consoleLog(err);
        return [];
      }
    },

    removePersonalEntriesFromGeneric(personalEntries, genericEntries) {
      const personalIds = new Set((personalEntries || []).map(entry => entry.id));
      return (genericEntries || []).filter(entry => !personalIds.has(entry.id));
    },

    onSearchScopeChange(nextScope) {
      if (this.searchScope === nextScope) {
        return;
      }

      this.searchScope = nextScope;
      this.resetExpandedEntries();
      this.applyCurrentViewAndFilter();
    },

    resetExpandedEntries() {
      // Clear visual selected/open state used by title highlighting and arrow direction
      this.openEntryStates = {};

      // Force-close any expanded content sections so they cannot stay visually out of sync
      const expanded = document.querySelectorAll(".dictionary-entries [id$='-content']");
      for (let i = 0; i < expanded.length; i++) {
        expanded[i].classList.add("content-closed");
      }
    },

    applyCurrentViewAndFilter() {
      this.personalDictionary = this.languageView === "translated"
        ? [...this.personalDictionaryTranslated]
        : [...this.personalDictionaryDefault];

      this.dictionary = this.languageView === "translated"
        ? [...this.dictionaryTranslated]
        : [...this.dictionaryDefault];

      this.filterEntries(this.query);
    },

    matchesQuery(entry, query) {
      if (!entry) {
        return false;
      }

      const loweredQuery = query.toLowerCase();
      const tags = Array.isArray(entry.tags) ? entry.tags : [];
      return (
        String(entry.title || "").toLowerCase().includes(loweredQuery) ||
        String(entry.description || "").toLowerCase().includes(loweredQuery) ||
        tags.some(t => String(t.name || "").toLowerCase().includes(loweredQuery))
      );
    },

    matchesScopeByEntryId(entryId, query, defaultById, translatedById) {
      const defaultEntry = defaultById.get(entryId);
      const translatedEntry = translatedById.get(entryId);

      if (this.searchScope === "default") {
        return this.matchesQuery(defaultEntry, query);
      }

      if (this.searchScope === "translated") {
        return this.matchesQuery(translatedEntry, query);
      }

      return (
        this.matchesQuery(defaultEntry, query) ||
        this.matchesQuery(translatedEntry, query)
      );
    },

    async filterEntries(query) {
      this.consoleLog(query);
      const normalizedQuery = String(query || "").trim();
      if (normalizedQuery === "") {
        this.filteredPersonalDictionary = this.personalDictionary;
        this.filteredDictionary = this.dictionary;
        return;
      }

      const personalDefaultById = new Map(this.personalDictionaryDefault.map(entry => [entry.id, entry]));
      const personalTranslatedById = new Map(this.personalDictionaryTranslated.map(entry => [entry.id, entry]));
      const genericDefaultById = new Map(this.dictionaryDefault.map(entry => [entry.id, entry]));
      const genericTranslatedById = new Map(this.dictionaryTranslated.map(entry => [entry.id, entry]));

      this.filteredPersonalDictionary = this.personalDictionary.filter(entry =>
        this.matchesScopeByEntryId(entry.id, normalizedQuery, personalDefaultById, personalTranslatedById)
      );

      this.filteredDictionary = this.dictionary.filter(entry =>
        this.matchesScopeByEntryId(entry.id, normalizedQuery, genericDefaultById, genericTranslatedById)
      );
    },
    resolveSearchByTagId(tagId, fallbackSearch) {
      if (!tagId) {
        return fallbackSearch || "";
      }

      const allEntries = [
        ...this.personalDictionaryDefault,
        ...this.personalDictionaryTranslated,
        ...this.dictionaryDefault,
        ...this.dictionaryTranslated
      ];
      for (let i = 0; i < allEntries.length; i++) {
        const entry = allEntries[i];
        if (!entry || !entry.tags || entry.tags.length === 0) {
          continue;
        }

        const matchedTag = entry.tags.find(tag => String(tag.id) === String(tagId));
        if (matchedTag && matchedTag.name) {
          return matchedTag.name;
        }
      }

      return fallbackSearch || "";
    },
    collapseContent(tag, entryId) {
      const content = document.getElementById(`${tag}-content`);
      if (!content) {
        return;
      }

      const isOpening = content.classList.contains("content-closed");
      content.classList.toggle("content-closed");

      if (isOpening) {
        this.$set(this.openEntryStates, tag, true);
      } else {
        this.$delete(this.openEntryStates, tag);
      }
    },
    isContentOpen(tag) {
      return !!this.openEntryStates[tag];
    },
    getEntryIconClass(tag) {
      return this.isContentOpen(tag)
        ? "dictionary-arrow dictionary-arrow-open"
        : "dictionary-arrow dictionary-arrow-closed";
    },
    linkify: function(text) {
      return linkifyStr(text);
    }
  }
};
</script>

<style scoped lang="scss">
.dictionary,
.parent-videos {
  width: 90%;
  margin: 0 auto;
  text-align: left;
  margin-bottom: 16px;
}

.translated-notice {
  font-size: 0.85em;
  color: #00a9b3;
  margin: 4px 0 0 0;
}

.dictionary-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
}

.dictionary-title-row h2 {
  margin: 0;
  flex-shrink: 0;
}

.language-select-wrap {
  width: 160px;
  flex-shrink: 0;
}

.search-scope-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  margin: -6px 0 10px;
}

.dictionary-option-label {
  font-size: 0.75rem;
  color: #888;
}

.scope-chip.v-chip--outlined {
  border-color: #afdfd3 !important;
  color: #9e9e9e !important;
}

.scope-chip.v-chip--outlined:hover {
  background-color: #d9f0ea !important;
  color: #00a9b3 !important;
}

/* Arrow points right by default and down only when the entry is open */
::v-deep .dictionary-arrow {
  transition: transform 0.18s ease;
}

::v-deep .dictionary-arrow-closed {
  transform: rotate(90deg);
}

::v-deep .dictionary-arrow-open {
  transform: rotate(0deg);
}

// Highlight the title box of the currently open dictionary entry
.active-entry {
  background-color: rgba(175, 223, 211, 0.35) !important;
  border-left: 4px solid #afdfd3;
}

.dictionary-entries {
  text-align: left;

  &.top {
    border-top: 3px solid #afdfd3;
    border-bottom: 3px solid #afdfd3;
  }

  &.bottom {
    border-bottom: 3px solid #afdfd3;
  }
}

.entry-content,
.dictionary-entries-container {
  width: 90%;
  margin: 0 auto;
}

.entry-content {
  margin-bottom: 16px;
  word-break: break-word;
}

.upload-container {
  width: 100%;
  overflow: hidden;
}

.upload-container img,
.upload-container video {
  max-width: 100%;
  margin: 0 auto;
}

.parent-video {
  height: 250px;
  border-radius: 5px;
  overflow: hidden;
}

.read-more-grid {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-evenly;
}

.description {
  word-break: break-word;
  white-space: pre-wrap; /* Preserve newlines */
}
</style>
